import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import { Grid, Tab, Tabs, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
// import { Theme } from "@mui/material/styles"; // Remove unused import
// import { SxProps } from "@mui/system"; // Remove unused import
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef, useCallback } from "react";
import ExtendModal from "./components/ExtendModal";
import Meta from "../../../components/common/Meta";
import VipLabel from "../../../components/common/VipLabel";
import { GradientTypography } from "../../../utils/gradientTypography";
import InputPrompt from "./components/InputPrompt";
import LatestVideoList from "./components/LatestVideoList";
import ParamsManager from "./components/ParamsManager";
import QuickSelection from "./components/QuickSelection";
import SampleVideoList from "./components/SampleVideoList";
import SelectVideoModel from "./components/SelectVideoModel";
import NotificationDisplay from "../../../components/common/NotificationDisplay";
import { ColumnCenteredBox, RowBox } from "../../../utils/styledBox"; // RowCenteredBox を削除
import Fade from "@mui/material/Fade";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import { uiHeight } from "../../../utils/uiHeight";
import ScrollableBox from "../../../components/common/ScrollableBox";
import { alpha } from "@mui/material/styles";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  // sx?: SxProps<Theme>; // sx is no longer needed here
}

function TabPanel(props: TabPanelProps) {
  const { children, index } = props;

  return (
    <Box
      role="tabpanel"
      id={`video-tabpanel-${index}`}
      aria-labelledby={`video-tab-${index}`}
      sx={{
        height: "100%", // Keep height
      }}
    >
      {children}
    </Box>
  );
}

const CARD_HEIGHT_ESTIMATE = 240;
const COLUMNS = 3; // 1行あたりの列数

const VideoGenerator = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [extendVideoUuid, setExtendVideoUuid] = useState<string>("");
  const [dynamicSize, setDynamicSize] = useState(9); // 初期値は9
  const tabPanelContainerRef = useRef<HTMLDivElement>(null); // コンテナへの参照

  // ExtendModalを表示するためのイベントリスナー
  const [videoUrl, setVideoUrl] = useState<string>("");

  // サブミット成功時にモーダルを閉じるための関数
  const closeExtendModal = () => {
    setExtendModalOpen(false);
  };

  useEffect(() => {
    const handleOpenExtendModal = (event: CustomEvent) => {
      const { videoUuid, videoUrl } = event.detail;
      setExtendVideoUuid(videoUuid);
      if (videoUrl) setVideoUrl(videoUrl);
      setExtendModalOpen(true);
    };

    // カスタムイベントをリッスン
    window.addEventListener("openExtendModal", handleOpenExtendModal as EventListener);

    // クリーンアップ
    return () => {
      window.removeEventListener("openExtendModal", handleOpenExtendModal as EventListener);
    };
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // サイズ計算ロジックを useCallback でメモ化
  const calculateSize = useCallback(() => {
    if (tabPanelContainerRef.current) {
      const containerHeight = tabPanelContainerRef.current.clientHeight;
      const rows = Math.max(1, Math.floor(containerHeight / CARD_HEIGHT_ESTIMATE));
      const newSize = Math.max(6, rows * COLUMNS);
      setDynamicSize(newSize);
    }
  }, []);

  // ResizeObserver でコンテナの高さを監視
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      calculateSize();
    });

    const containerElement = tabPanelContainerRef.current;
    if (containerElement) {
      observer.observe(containerElement);
      // 初期サイズ計算
      calculateSize();
    }

    // クリーンアップ
    return () => {
      if (containerElement) {
        observer.unobserve(containerElement);
      }
    };
  }, [calculateSize]); // calculateSize が変更されたら再実行

  return (
    <Box>
      <Meta title={t("juno.video.title")} route={"Studio JUNO by Tenak AI"} />
      <Container maxWidth="xl">
        <Grid container spacing={2} sx={{ my: 2 }}>
          <Grid size={{ xs: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <MovieFilterIcon sx={{ mr: 2 }} fontSize={"medium"} />
              <GradientTypography component="h1" variant="h5" align="center" mb={0.5}>
                {t("juno.video.title")}
              </GradientTypography>
              <VipLabel sx={{ ml: 2 }} />
            </Box>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Fade in={true} timeout={1000}>
              <RowBox sx={{ justifyContent: "flex-end", alignItems: "flex-end", height: "100%" }}>
                <NotificationDisplay notificationKey="juno-video" size="small" />
              </RowBox>
            </Fade>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
            <QuickSelection />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <SelectVideoModel />
            <ScrollableBox
              sx={{ height: uiHeight(-345), overflowY: "auto", overflowX: "hidden", mt: 2, borderRadius: 1 }}
            >
              <ColumnCenteredBox
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.3),
                  backdropFilter: "blur(10px)",
                  p: 3,
                }}
              >
                <RowBox mb={4}>
                  <SettingsApplicationsIcon color={"secondary"} sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">{t("juno.video.params.title")}</Typography>
                </RowBox>

                <ParamsManager />
              </ColumnCenteredBox>
            </ScrollableBox>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 9,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", height: uiHeight(-235) }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2, flexShrink: 0 }}>
                <Box sx={{ flexShrink: 0 }}>
                  <InputPrompt />
                </Box>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="video list tabs">
                  <Tab
                    icon={<VideoLibraryIcon />}
                    iconPosition="start"
                    label="My Video"
                    id="video-tab-0"
                    aria-controls="video-tabpanel-0"
                  />
                  <Tab
                    icon={<SlideshowIcon />}
                    iconPosition="start"
                    label={t("juno.video.sampleVideo")}
                    id="video-tab-1"
                    aria-controls="video-tabpanel-1"
                  />
                </Tabs>
              </Box>
              <Box
                ref={tabPanelContainerRef}
                sx={{ flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
              >
                {tabValue === 0 && (
                  <TabPanel index={0}>
                    <LatestVideoList size={dynamicSize} />
                  </TabPanel>
                )}
                {tabValue === 1 && (
                  <TabPanel index={1}>
                    <SampleVideoList />
                  </TabPanel>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* 拡張動画モーダル */}
      <ExtendModal open={extendModalOpen} onClose={closeExtendModal} videoUuid={extendVideoUuid} videoUrl={videoUrl} />
    </Box>
  );
};
export default VideoGenerator;
